import React, { useState, useEffect, useRef } from 'react';
import { useDiagnosticConversation } from '../hooks/useDiagnosticConversation';
import './DiagnosticChat.scss';

interface DiagnosticChatProps {
  organizationId: string;
  customerId?: string | null;
  machineId?: number | null;
  make: string;
  model: string;
  onComplete: (conversationId: string, quoteId: number | null) => void;
}

const DiagnosticChat: React.FC<DiagnosticChatProps> = ({
  organizationId,
  customerId,
  machineId,
  make,
  model,
  onComplete
}) => {
  const {
    messages,
    conversationId,
    quoteId,
    loading,
    error,
    isConversationComplete,
    quickAnswers,
    startConversation,
    continueConversation,
    clearError
  } = useDiagnosticConversation(organizationId, customerId, machineId);

  const [userInput, setUserInput] = useState('');
  const [hasStarted, setHasStarted] = useState(false);
  const [isFirstQuestion, setIsFirstQuestion] = useState(true);
  const [loadingPhrase, setLoadingPhrase] = useState('Thinking...');
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Loading phrases for better UX
  const loadingPhrases = [
    "Thinking...",
    "Analyzing the issue...",
    "Gathering information...",
    "Processing your response...",
    "Formulating next question...",
    "Reviewing diagnostic data...",
    "Evaluating the problem..."
  ];

  // Cycle through loading phrases while loading (only after first question)
  useEffect(() => {
    if (!loading || isFirstQuestion) return;

    let phraseIndex = 0;
    setLoadingPhrase(loadingPhrases[0]);

    const interval = setInterval(() => {
      phraseIndex = (phraseIndex + 1) % loadingPhrases.length;
      setLoadingPhrase(loadingPhrases[phraseIndex]);
    }, 8000); // Change phrase every 8 seconds

    return () => clearInterval(interval);
  }, [loading, isFirstQuestion]);

  // Auto-start conversation when component mounts
  useEffect(() => {
    if (!hasStarted && make && model) {
      setHasStarted(true);
      startConversation(make, model);
    }
  }, [hasStarted, make, model, startConversation]);

  // Notify parent when conversation is complete
  useEffect(() => {
    if (isConversationComplete && conversationId) {
      onComplete(conversationId, quoteId);
    }
  }, [isConversationComplete, conversationId, quoteId, onComplete]);

  // Scroll to bottom of messages container when messages change (without scrolling the page)
  useEffect(() => {
    if (messages.length > 0 && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userInput.trim() || loading) return;

    const answer = userInput.trim();
    setUserInput('');

    // After first response, we're no longer on the first question
    setIsFirstQuestion(false);

    await continueConversation(answer);
  };

  const handleQuickAnswer = async (answer: string) => {
    if (loading) return;

    setIsFirstQuestion(false);
    await continueConversation(answer);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  return (
    <div className="diagnostic-chat">
      <div className="diagnostic-chat__header">
        <h3>Equipment Diagnostic</h3>
        <p className="diagnostic-chat__subtitle">
          Tell us about the issue with your {make} {model}
        </p>
      </div>

      <div className="diagnostic-chat__messages" ref={messagesContainerRef}>
        {messages.map((message) => (
          <div
            key={message.id}
            className={`diagnostic-chat__message diagnostic-chat__message--${message.role}`}
          >
            <div className="diagnostic-chat__message-content">
              {message.content}
            </div>
            <div className="diagnostic-chat__message-timestamp">
              {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        ))}

        {loading && (
          <div className="diagnostic-chat__message diagnostic-chat__message--assistant">
            <div className="diagnostic-chat__message-content">
              <div className="diagnostic-chat__typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
              {!isFirstQuestion && (
                <span className="diagnostic-chat__loading-text">{loadingPhrase}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="diagnostic-chat__error">
          <span>{error}</span>
          <button onClick={clearError} className="diagnostic-chat__error-close">×</button>
        </div>
      )}

      {isConversationComplete ? (
        <div className="diagnostic-chat__complete">
          <div className="diagnostic-chat__complete-icon">✓</div>
          <p className="diagnostic-chat__complete-message">
            Thank you! We're processing your diagnostic. You can continue with your request.
          </p>
        </div>
      ) : (
        <>
          {quickAnswers && quickAnswers.length > 0 && (
            <div className="diagnostic-chat__quick-answers">
              {quickAnswers.map((answer) => (
                <button
                  key={answer}
                  type="button"
                  className="diagnostic-chat__quick-answer-btn"
                  onClick={() => handleQuickAnswer(answer)}
                  disabled={loading}
                >
                  {answer}
                </button>
              ))}
            </div>
          )}
          <form className="diagnostic-chat__input-form" onSubmit={handleSubmit}>
            <textarea
              className="diagnostic-chat__input"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={loading ? "Processing..." : "Type your response..."}
              disabled={loading || !hasStarted}
              rows={2}
            />
            <button
              type="submit"
              className="diagnostic-chat__send-btn"
              disabled={!userInput.trim() || loading || !hasStarted}
            >
              Send
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default DiagnosticChat;
